import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, RefreshCw, Phone, Mail } from "lucide-react";
import { api } from "@/lib/auth";
import { BUSINESS } from "@/lib/site";

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/enquiries");
      setEnquiries(r.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/admin/login");
        return;
      }
      setError("Could not load enquiries. Try refresh.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    document.title = "Enquiries Dashboard | SUDHEER ELECTRONIC";
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        load();
      })
      .catch(() => navigate("/admin/login"));
  }, [navigate, load]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    navigate("/admin/login");
  };

  if (!user) {
    return <div className="min-h-screen bg-[#f4f7fa] flex items-center justify-center text-sm text-slate-500" data-testid="admin-loading">Checking access...</div>;
  }

  return (
    <div className="min-h-screen bg-[#f4f7fa] text-slate-900" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-700 rounded-md flex items-center justify-center text-white font-black text-xs">SE</div>
            <div>
              <span className="text-sm font-black tracking-tight text-slate-950 block leading-tight">Enquiries Dashboard</span>
              <span className="text-[10px] text-slate-500 block">{user.email}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" data-testid="admin-view-site-link" className="text-xs font-bold text-slate-600 hover:text-blue-700 px-3 py-2 transition-colors">View Site</Link>
            <button type="button" onClick={load} data-testid="admin-refresh-button"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-blue-700 border border-blue-200 rounded hover:bg-blue-50 transition-colors">
              <RefreshCw size={13} /> Refresh
            </button>
            <button type="button" onClick={logout} data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-slate-800 hover:bg-slate-700 rounded transition-colors">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-black tracking-tight text-slate-950">All Enquiries</h1>
          <span className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-3 py-1" data-testid="enquiry-count">
            {enquiries.length} total
          </span>
        </div>

        {error && <p className="text-xs font-semibold text-red-600 mb-4" data-testid="admin-error">{error}</p>}
        {loading ? (
          <p className="text-sm text-slate-500">Loading...</p>
        ) : enquiries.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-10 text-center" data-testid="enquiries-empty">
            <p className="text-sm font-bold text-slate-950 mb-1">No enquiries yet</p>
            <p className="text-xs text-slate-500">New website enquiries will appear here and in your email inbox.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {enquiries.map((en) => (
              <div key={en.id} data-testid={`enquiry-item-${en.id}`} className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                  <div>
                    <span className="text-sm font-bold text-slate-950">{en.name}</span>
                    {en.product && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-blue-700 bg-blue-50 rounded px-2 py-0.5">{en.product}</span>}
                  </div>
                  <span className="text-[11px] text-slate-500">{new Date(en.created_at).toLocaleString("en-IN")}</span>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed mb-3">{en.message}</p>
                <div className="flex flex-wrap gap-2">
                  <a href={`tel:+91${en.phone.replace(/\D/g, "").slice(-10)}`} data-testid={`enquiry-call-${en.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold text-blue-700 border border-blue-200 rounded hover:bg-blue-50 transition-colors">
                    <Phone size={12} /> {en.phone}
                  </a>
                  <a href={`https://wa.me/91${en.phone.replace(/\D/g, "").slice(-10)}`} target="_blank" rel="noopener noreferrer"
                    data-testid={`enquiry-whatsapp-${en.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-50 transition-colors">
                    <Mail size={12} /> WhatsApp
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-[11px] text-slate-400 mt-8 text-center">
          Every enquiry is also emailed to {BUSINESS.email}
        </p>
      </main>
    </div>
  );
}
